import type { ReactNode } from "react";

// Lucide-style 24px line icons, 2px strokes.
function mk(paths: ReactNode, opts: { fill?: boolean } = {}) {
  return function Icon({
    size = 18,
    stroke = 2,
    ...rest
  }: Omit<React.SVGProps<SVGSVGElement>, "stroke"> & { size?: number; stroke?: number }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={opts.fill ? "currentColor" : "none"}
        stroke={opts.fill ? "none" : "currentColor"}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        {paths}
      </svg>
    );
  };
}

export const Icons = {
  Sparkle: mk(
    <g>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
      <circle cx="12" cy="12" r="3.2" />
    </g>
  ),
  Download: mk(
    <g>
      <path d="M12 4v12M8 12l4 4 4-4" />
      <path d="M5 20h14" />
    </g>
  ),
  Refresh: mk(<path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />),
  Calendar: mk(
    <g>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </g>
  ),
  Music: mk(
    <g>
      <path d="M9 18V5l11-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="17" cy="16" r="3" />
    </g>
  ),
  Check: mk(<path d="M20 6 9 17l-5-5" />),
  BadgeCheck: mk(
    <g>
      <path d="M12 2.5l2.3 1.7 2.9-.3 1 2.7 2.7 1-.3 2.9 1.7 2.3-1.7 2.3.3 2.9-2.7 1-1 2.7-2.9-.3L12 21.5l-2.3-1.7-2.9.3-1-2.7-2.7-1 .3-2.9L2 12l1.7-2.3-.3-2.9 2.7-1 1-2.7 2.9.3z" />
      <path d="M9 12l2 2 4-4" />
    </g>
  ),
  Book: mk(<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14Z" />),
  Hand: mk(<path d="M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v2M10 10.5V6a2 2 0 0 0-4 0v8a8 8 0 0 0 8 8h2a8 8 0 0 0 8-8v-1a2 2 0 0 0-4 0" />),
  Chalice: mk(
    <g>
      <path d="M7 4h10M8 4c0 5 1.5 7 4 7s4-2 4-7M12 11v6M8 20h8M9 20c0-2 1.3-3 3-3s3 1 3 3" />
    </g>
  ),
  External: mk(
    <g>
      <path d="M15 3h6v6M21 3l-9 9M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </g>
  ),
};

export function WhatsAppGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M.06 24l1.69-6.16a11.87 11.87 0 0 1-1.6-5.95C.15 5.33 5.5 0 12.07 0a11.82 11.82 0 0 1 8.41 3.49 11.8 11.8 0 0 1 3.48 8.41c0 6.56-5.34 11.9-11.9 11.9a11.9 11.9 0 0 1-5.69-1.45L.06 24zM6.6 20.13l.36.22a9.86 9.86 0 0 0 5.03 1.38h.01c5.45 0 9.89-4.43 9.9-9.88a9.82 9.82 0 0 0-2.9-7 9.82 9.82 0 0 0-7-2.9c-5.46 0-9.9 4.43-9.9 9.88 0 1.86.52 3.68 1.51 5.25l.24.38-1 3.65 3.75-.98zM17.5 14.3c-.08-.12-.27-.2-.56-.34-.29-.15-1.7-.84-1.96-.93-.26-.1-.45-.15-.64.14-.19.29-.74.93-.9 1.12-.17.19-.33.21-.62.07-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.6-2-.17-.29-.02-.45.12-.6.13-.13.29-.33.43-.5.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.15-.64-1.55-.88-2.12-.23-.56-.47-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.38s1.02 2.76 1.17 2.95c.14.19 2.01 3.08 4.88 4.32.68.29 1.21.47 1.63.6.68.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36z"/>
    </svg>
  );
}

export function YouTubeGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.72} viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M27.4 3.1a3.5 3.5 0 0 0-2.46-2.48C22.76 0 14 0 14 0S5.24 0 3.06.62A3.5 3.5 0 0 0 .6 3.1C0 5.3 0 10 0 10s0 4.7.6 6.9a3.5 3.5 0 0 0 2.46 2.48C5.24 20 14 20 14 20s8.76 0 10.94-.62a3.5 3.5 0 0 0 2.46-2.48C28 14.7 28 10 28 10s0-4.7-.6-6.9Z"
        fill="currentColor"
      />
      <path d="M11.2 14.3 18.5 10l-7.3-4.3v8.6Z" fill="#fff" />
    </svg>
  );
}
